import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchSystemConfig, invalidateSystemConfig } from '@/lib/fetchSystemConfig';
import type { SystemConfig } from '@/types/systemConfig';

interface SystemConfigContextValue {
  config: SystemConfig | null;
  loading: boolean;
  refreshConfig: () => Promise<void>;
}

const SystemConfigContext = createContext<SystemConfigContextValue | null>(null);

export function SystemConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const loadConfig = useCallback(async () => {
    const data = await fetchSystemConfig();
    setConfig(data);
  }, []);

  useEffect(() => {
    loadConfig().finally(() => setLoading(false));
  }, [loadConfig]);

  const refreshConfig = useCallback(async () => {
    invalidateSystemConfig();
    await loadConfig();
  }, [loadConfig]);

  return (
    <SystemConfigContext.Provider value={{ config, loading, refreshConfig }}>
      {children}
    </SystemConfigContext.Provider>
  );
}

export function useSystemConfig() {
  const ctx = useContext(SystemConfigContext);
  if (!ctx) {
    throw new Error('useSystemConfig yalnızca SystemConfigProvider içinde kullanılabilir.');
  }
  return ctx;
}
